import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DatePicker,
  Input,
  Select,
  Spin,
  Checkbox,
  Button,
  notification,
  Modal,
} from "antd";
import styled from "styled-components";
import { ReactComponent as SearchIcon } from "assets/icons/UI/search.svg";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import Table from "atoms/Table";
import queryKeys from "common/queryKey";
import moment from "moment";
import { useScrollFetchPost2 } from "hooks/useScrollFetchPost2";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import { useUpdate } from "hooks/useUpdate";
import { useQueryClient } from "react-query";
import {
  CheckCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { CALL_API } from "common/API";

const { RangePicker } = DatePicker;

const initialTimeSheetColumns = [
  {
    title: null,
    dataIndex: "checkbox",
    render: (_, record) => null,
    width: 50,
  },
  {
    title: "Period",
    dataIndex: "period",
    key: "period",
    width: 230,
  },
  {
    title: "Name",
    dataIndex: "employee_name",
    key: "employee_name",
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
  },
  {
    title: "Timesheet Hours",
    dataIndex: "timesheet_hours",
    key: "timesheet_hours",
    align: "center",
  },
  {
    title: "Total Claims",
    dataIndex: "total_claims",
    key: "total_claims",
    align: "center",
  },
  {
    title: "Total Tasks",
    dataIndex: "total_tasks",
    key: "total_tasks",
    align: "center",
  },
];

const formatAmount = (value) => {
  const num = Number(value?.toString().replace(/[^0-9.-]+/g, ""));
  return isNaN(num)
    ? value
    : num.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

const generateWeeksBetweenDates = (dateRange) => {
  if (dateRange?.length === 0 || dateRange === undefined || dateRange === null)
    return [];
  let startWeek = moment(dateRange[0]).startOf("isoWeek");
  const endWeek = moment(dateRange[1]).endOf("isoWeek");
  const weeks = [];
  while (startWeek <= endWeek) {
    let endOfWeek = moment(startWeek).add(6, "days"); // create a new moment object for the end of the week
    weeks.push(
      `${startWeek.format("YYYYMMMDD").toUpperCase()}-${endOfWeek
        .format("YYYYMMMDD")
        .toUpperCase()}`
    );
    startWeek = startWeek.add(1, "week");
  }
  return weeks;
};

const TimeSheetApproval = ({ timeSheetPermission }) => {
  const history = useHistory();
  const [dateRange, setDateRange] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [status, setStatus] = useState("pending");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const [timeSheetColumns, setTimeSheetColumns] = useState(
    initialTimeSheetColumns
  );

  const { getAdminApprovalTimesheet, updateTimesheet, getTimesheet } =
    queryKeys;
  const userInfo = useSelector(getUserInfo);

  const { key, url } = getAdminApprovalTimesheet(
    JSON.stringify(generateWeeksBetweenDates(dateRange))
  );

  const [isModalBulkApproveOpen, setIsModalBulkApproveOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [approvalStatuses, setApprovalStatuses] = useState({});

  const {
    data,
    isLoading: loader,
    refetch,
  } = useScrollFetchPost2(`${key}-${status}`, {
    endPoint: url,
    queryParam: "LastEvaluatedKey",
    extra: {
      weeks: generateWeeksBetweenDates(dateRange),
      statusArray: [status],
      employeeId: [],
      userId: userInfo?.userid,
    },
  });

  const invalidateTimeSheet = useCallback(async () => {
    setLoading(true);
    try {
      await refetch();
    } finally {
      setLoading(false);
    }
  }, [refetch]);

  const timesheetsData = useMemo(() => {
    if (!data) return [];
    return data?.pages?.reduce((acc, page) => {
      if (page?.response?.response === undefined) return acc;
      return [...acc, ...page?.response?.response];
    }, []);
  }, [data]);

  const [timeSheets, setTimeSheets] = useState([]);

  useEffect(() => {
    if (timesheetsData) {
      const filteredData = timesheetsData?.map((entry) => ({
        ...entry,
        timeSheet: entry.timeSheet?.filter(
          (sheet) =>
            status.includes(sheet.status) &&
            sheet.personInCharge === userInfo.userid
        ),
      }))?.filter((entry) => entry.timeSheet?.length > 0)
          ?.filter((timesheet) => timesheet?.employeeName
            ?.toLowerCase()
            .includes(searchName.toLowerCase())
        )?.map((timesheet) => {
          const workingHours = timesheet?.timeSheet?.reduce((acc, day) => {
            return (
              acc +
              (day?.mon || 0) +
              (day?.tue || 0) +
              (day?.wed || 0) +
              (day?.thu || 0) +
              (day?.fri || 0) +
              (day?.sat || 0) +
              (day?.sun || 0)
            );
          }, 0);

          const totalClaims = timesheet?.timeSheet?.reduce((acc, day) => {
            const claims = day?.claims?.reduce((acc, claim) => {
              return acc + (parseInt(claim?.amount) || 0);
            }, 0);
            return acc + claims;
          }, 0);

          const hasTask = (task) => {
            return task !== null ? 1 : 0;
          };

          const totalTasks = timesheet?.timeSheet?.reduce((acc, day) => {
            return acc + hasTask(day?.task);
          }, 0);

          const startWeek = timesheet?.week?.split("-")[0];
          const endWeek = timesheet?.week?.split("-")[1];
          const formattedPeriod = `${moment(startWeek, "YYYYMMMDD").format(
            "DD-MMM-YYYY"
          )} to ${moment(endWeek, "YYYYMMMDD").format("DD-MMM-YYYY")}`;

          const hasNc = timesheet?.timeSheet?.some(
            (sec) => sec.engagement === "nc"
          );

          const hasC = timesheet?.timeSheet?.some(
            (sec) => sec.engagement !== "nc"
          );

          const hasBoth = hasNc && hasC;

          const type = hasBoth ? "NC & C" : hasNc ? "NC" : "C";
          return {
            key: timesheet?.timeSheetId, // Ensure each item has a unique key for checkbox handling
            timesheet_id: timesheet?.timeSheetId,
            week: timesheet?.week,
            type: type,
            employeeId: timesheet?.employeeId,
            employee_name: (
              <div className="employee-name">{timesheet?.employeeName}</div>
            ),
            period: <div className="period">{formattedPeriod}</div>,
            timesheet_hours: workingHours,
            total_claims: formatAmount(totalClaims),
            total_tasks: totalTasks,
            status: timesheet?.status, // Add status for conditional rendering of checkbox
          };
        });
      setTimeSheets(filteredData);
      // Ensure selectedRowKeys are within the new data set
      setSelectedRowKeys((prevKeys) =>
        prevKeys.filter((key) => filteredData.some((item) => item.key === key))
      );
    }
  }, [timesheetsData, searchName, invalidateTimeSheet]);

  const handleSelectAllCheckBoxchange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const pendingTimesheets = timeSheets.filter(
        (record) => record.status === "pending"
      );
      setSelectedRowKeys(pendingTimesheets.map((record) => record.key));
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSingleCheckBoxchange = (e, record) => {
    const checked = e.target.checked;
    setSelectedRowKeys((prevSelected) => {
      if (checked) {
        return [...prevSelected, record.key];
      } else {
        return prevSelected.filter((key) => key !== record.key);
      }
    });
  };

  useEffect(() => {
    const newColumns = initialTimeSheetColumns.map((col) => {
      if (col.dataIndex === "checkbox") {
        return {
          ...col,
          title:
            status === "pending" ? (
              <Checkbox
                onClick={(e) => e.stopPropagation()}
                checked={
                  timeSheets.filter((item) => {
                    const timeSheetEntry = timesheetsData.find(
                      (t) => t.timeSheetId === item.timesheet_id
                    )?.timeSheet?.some(
                      item => item.personInCharge === userInfo?.userid && item.status === "pending"
                    );
                    return timeSheetEntry;
                  }).length > 0 &&
                  selectedRowKeys.length ===
                    timeSheets.filter((item) => {
                      const timeSheetEntry = timesheetsData.find(
                        (t) => t.timeSheetId === item.timesheet_id
                      )?.timeSheet?.some(
                        item => item.personInCharge === userInfo?.userid && item.status === "pending"
                      );
                      return timeSheetEntry;
                    }).length
                }
                indeterminate={
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length <
                    timeSheets.filter((item) => {
                      const timeSheetEntry = timesheetsData.find(
                        (t) => t.timeSheetId === item.timesheet_id
                      )?.timeSheet?.some(
                        item => item.personInCharge === userInfo?.userid && item.status === "pending"
                      );
                      return timeSheetEntry;
                    }).length
                }
                onChange={handleSelectAllCheckBoxchange}
              />
            ) : null,
          render: (_, record) => {
            const timeSheetEntry = timesheetsData.find(
              (item) => item.timeSheetId === record.timesheet_id
            )?.timeSheet?.filter(
              item => item.personInCharge === userInfo?.userid && item.status === "pending"
            );
            if ((timeSheetEntry?.length === 0) || (status !== "pending")) {
              return null;
            }
            return (
              <Checkbox
                onClick={(e) => e.stopPropagation()}
                checked={selectedRowKeys.includes(record.key)}
                onChange={(e) => handleSingleCheckBoxchange(e, record)}
              />
            );
          },
        };
      }
      return col;
    });

    setTimeSheetColumns(newColumns);
  }, [timeSheets, selectedRowKeys, status, timesheetsData]);

  const { week } = useParams();
  const { mutate: approveUpdate } = useUpdate(
    updateTimesheet.key,
    updateTimesheet.url
  );

  const approveTimesheet = async () => {
    if (!selectedRowKeys?.length) {
      notification.warning({ message: "No timesheets selected" });
      return;
    }
    setLoading(true);
    for (const id of selectedRowKeys) {
      const timesheetIndex = timesheetsData.findIndex(item => item.timeSheetId === id);
      if (timesheetIndex === -1) continue;
      const oldData = timesheetsData[timesheetIndex];
      const updatedTimeSheet = await Promise.all((oldData?.timeSheet || []).map(async (item) => {
          if (item.personInCharge === userInfo?.userid) {
            return {
              ...item,
              status: "approved",
              approval: {
                by: userInfo.employer_id,
                date: moment().format("DD-MM-YYYY"),
              },
            };
          }
          return item;
        })
      );
      const payload = {
        ...oldData,
        updatedTimeSheet: updatedTimeSheet?.filter(item => item.personInCharge === userInfo?.userid)?.map(item => 
        ({ ...item, status: "approved" })) || [],
        timeSheet: updatedTimeSheet,
        status: updatedTimeSheet?.some(entry => entry.status === "pending") ? "pending" : "approved",
      };
      
      setApprovalStatuses(prev => ({ ...prev, [id]: "pending" }));
      await new Promise((resolve) => {
        approveUpdate(payload, {
          onSuccess: () => {
            setApprovalStatuses(prev => ({ ...prev, [id]: "approved" }));
            resolve(); // just resolve
          },
          onError: () => {
            setApprovalStatuses(prev => ({ ...prev, [id]: "declined" }));
            resolve(); // still resolve even if failed
          }
        });
      });
    }
    // ✅ Only run after all approvals done
    await queryClient.invalidateQueries([getTimesheet.key, `week:${week}`]);
    await invalidateTimeSheet();
    notification.success({ message: "Bulk approval completed" });
    setIsModalBulkApproveOpen(false); // ✅ Close modal only after all done
    setLoading(false)
  };

  const handleBulkApprovalModal = () => {
    setIsModalBulkApproveOpen(true);
  };

  useEffect(() => {
    const selectedData = timeSheets.filter((row) =>
      selectedRowKeys.includes(row.key)
    );
    setSelectedRowData(selectedData);
  }, [selectedRowKeys, timeSheets]);

  return (
    <>
      {isModalBulkApproveOpen && (
        <>
          <Modal
            title="Approve Selected Timesheets"
            visible={isModalBulkApproveOpen}
            // onCancel={() => setIsModalBulkApproveOpen(false)}
            footer={null}
            onCancel={() => {
              if (!loading) setIsModalBulkApproveOpen(false); // block cancel while loading
            }}
            closable={!loading}
            maskClosable={!loading}
          >
            <div style={{ overflowY: "scroll", maxHeight: "300px" }}>
              {selectedRowData?.map((row) => {
                const timesheetId = row?.timeSheetId ?? row?.key;
                const status = approvalStatuses[timesheetId];
                return (
                  <div
                    key={timesheetId}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      marginBottom: "8px",
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ flex: 2 }}>
                      <strong style={{ color: "#397ef3", whiteSpace: "nowrap",overflow: "hidden",textOverflow: "ellipsis" }}>
                        {row?.employee_name?.props?.children ??
                          row?.employee_name}
                      </strong>
                    </div>
                    <div style={{  flex: 2,marginLeft: 20,whiteSpace: "nowrap", }}>
                      {row?.period?.props?.children ?? row?.period}
                    </div>
                    <div style={{ flex: 2, marginLeft: 20,whiteSpace: "nowrap" }}>{row?.type}</div>
                    <div style={{ flex: 2 }}>{row?.timesheet_hours}</div>
                    <div style={{ marginLeft: 8 }}>
                      {status === "pending" && (
                        <LoadingOutlined style={{ color: "#1890ff" }} />
                      )}
                      {status === "approved" && (
                        <CheckCircleOutlined style={{ color: "green" }} />
                      )}
                      {status === "declined" && (
                        <CloseCircleOutlined style={{ color: "red" }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
              <Button
                key="cancel"
                onClick={() => setIsModalBulkApproveOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              
              <Button
                type="primary"
                onClick={() => {
                  approveTimesheet();
                }}
                loading={loading}
              >
                Approve All
              </Button>
            </div>
          </Modal>
        </>
      )}
      <TimeSheetWrap>
        <div className="filters">
          <Input
            placeholder="Search by name"
            style={{
              width: 300,
            }}
            value={searchName}
            onChange={(e) => {
              setSearchName(e.target.value);
            }}
            prefix={<SearchIcon />}
          />

          <RangePicker
            value={dateRange}
            onChange={(e) => {
              setDateRange(e);
            }}
          />

          <Select
            value={status}
            style={{ width: 120 }}
            onChange={(value) => {
              setStatus(value);
            }}
            options={[
              { label: "Pending", value: "pending" },
              { label: "Approved", value: "approved" },
              { label: "Declined", value: "declined" },
            ]}
          />
        </div>
        {status === "pending" && (
          <Button
            className="mt-2"
            style={{ marginLeft: 0 }}
            disabled={!selectedRowKeys?.length > 0}
            onClick={handleBulkApprovalModal}
            type="primary"
          >
            {" "}
            Approve{" "}
          </Button>
        )}
        <div className="timesheet">
          <Spin spinning={loader || loading}>
            <Table
              dataSource={timeSheets || []}
              columns={timeSheetColumns}
              onRow={(record) => {
                return {
                  onClick: (event) => {
                    history.push(
                      `/timesheet-detailed/${record?.week}/${record?.employeeId}/${record?.timesheet_id}/${status}`
                    );
                  },
                };
              }}
              rowClassName={(record, index) =>
                index % 2 === 0 ? "" : "non-white"
              }
              rowKey="key"
              pagination={false}
            />
          </Spin>
        </div>
      </TimeSheetWrap>
    </>
  );
};

export default TimeSheetApproval;

const TimeSheetWrap = styled.div`
  .filters {
    display: flex;
    gap: 12px;
    justify-content: start;
    align-items: center;
    flex-wrap: wrap;
  }
  .timesheet {
    padding-top: 10px;
  }
  .action-buttons {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    font-weight: 600;
  }
  .period {
    font-weight: 600;
    color: #397ef3;
    cursor: pointer;
  }
  .employee-name {
    font-weight: 600;
  }
`;